import React, { useRef, useState, useEffect, KeyboardEvent, ClipboardEvent } from 'react';

interface OtpInputProps {
  length?: number;
  onComplete: (code: string) => void;
}

export const OtpInput: React.FC<OtpInputProps> = ({ length = 6, onComplete }) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus the first input on initial mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (value: string, index: number) => {
    // Only allow numeric input
    if (isNaN(Number(value))) return;

    const newOtp = [...otp];
    // Take only the last character if user types quickly over an existing value
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Trigger onComplete callback if all boxes are filled
    const combinedCode = newOtp.join('');
    onComplete(combinedCode);

    // Auto-focus next input box if value is filled
    if (value && index < length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0 && inputRefs.current[index - 1]) {
        // Move focus to previous input if current is already empty
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Only look for numbers
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    const pasteArray = pastedData.split('').slice(0, length);

    pasteArray.forEach((char, i) => {
      newOtp[i] = char;
    });

    setOtp(newOtp);

    // Focus the last filled or last available input
    const focusIndex = Math.min(pasteArray.length, length - 1);
    inputRefs.current[focusIndex]?.focus();

    const combinedCode = newOtp.join('');
    onComplete(combinedCode);
    
  };

  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
      {otp.map((digit, index) => (
        <input
          key={index}
          type="text"
          inputMode="numeric" // Triggers numeric keyboard on mobile devices
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          ref={(el) => (inputRefs.current[index] = el)}
          onChange={(e) => handleChange(e.target.value, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          style={{
            width: '60px',
            height: '60px',
            textAlign: 'center',
            fontSize: '1.6rem',
            border: '1px solid #ccc',
            borderRadius: '6px',
            outline: 'none',
          }}
        />
      ))}
    </div>
  );
};
